import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import MarketIntelligenceGraph from './MarketIntelligenceGraph';
import TalentPoolInsights from './TalentPoolInsights';

// Utility functions for fetching images
const getProfilePictureFromLinkedIn = (linkedinUrl) => {
  // This is a placeholder - in a real implementation, you'd need to:
  // 1. Use LinkedIn's API (requires authentication)
  // 2. Or scrape the profile page (requires backend proxy due to CORS)
  // 3. Or store profile pictures in your database when collecting data

  // For now, return a placeholder or use a service like UI Faces
  return `https://ui-avatars.com/api/?name=${encodeURIComponent('Profile')}&background=random&size=64`;
};

const getCompanyLogo = async (companyName) => {
  // Use backend API to fetch company logos via Logo.dev
  if (!companyName) {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent('Company')}&background=random&size=48&bold=true`;
  }

  try {
    const response = await fetch(`/api/company-logo/${encodeURIComponent(companyName)}`);
    if (response.ok) {
      const data = await response.json();
      return data.logo_url;
    }
  } catch (error) {
    console.error('Error fetching company logo:', error);
  }

  // Fallback to generated avatar
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(companyName)}&background=random&size=48&bold=true`;
};

// Icons
const MarketIntelligenceIcon = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const SalaryIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
  </svg>
);

const PeopleIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const VoiceIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
);

const SendIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

/** UI commodity chip label → API ``CategoryKey`` (Firestore ``category`` / ``tags`` taxonomy). */
const PEOPLE_MOVE_CATEGORY_TO_KEY = {
  Energy: 'energy',
  Metals: 'metals',
  'Agriculture & Softs': 'softs_ags',
  'Freight & Shipping': 'freight_shipping',
  'Cross-Commodity & Finance': 'cross_commodity',
  'Carbon & Renewables': 'carbon_renewables',
  'Other Specialties': 'non_trading',
};

const formatPeopleMoveCategoryLabel = (key) => {
  if (!key || typeof key !== 'string') return null;
  const k = key.toLowerCase();
  const labels = {
    energy: 'Energy',
    metals: 'Metals',
    softs_ags: 'Agriculture & Softs',
    freight_shipping: 'Freight & Shipping',
    cross_commodity: 'Cross-Commodity',
    non_trading: 'Non-trading',
    carbon_renewables: 'Carbon & Renewables',
    other: 'Other',
  };
  return labels[k] || key.replace(/_/g, ' ');
};

export default function MarketIntelligence() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const getAuthToken = async () => {
    if (currentUser && typeof currentUser.getIdToken === 'function') {
      return currentUser.getIdToken();
    }
    return (
      localStorage.getItem('token') ||
      localStorage.getItem('firebaseToken') ||
      localStorage.getItem('idToken') ||
      ''
    );
  };
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [currentFilters, setCurrentFilters] = useState({});
  const [conversationState, setConversationState] = useState('initial'); // 'initial', 'awaiting_industry', 'awaiting_filters'
  const [pendingQuery, setPendingQuery] = useState('');
  const [miView, setMiView] = useState('chat'); // 'chat' | 'graph'
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);

  // Mock data for demonstration
  const mockSalaryData = {
    "Senior Grains Trader": {
      "Singapore": {
        "base_salary": { "min": 120000, "max": 180000, "median": 150000 },
        "total_comp": { "min": 180000, "max": 250000, "median": 215000 },
        "bonus_percentage": { "min": 30, "max": 50, "median": 40 },
        "currency": "SGD",
        "experience_years": 10,
        "market_trend": "+15% from 2024",
        "sample_size": 45,
        "confidence_score": 0.85
      }
    }
  };

  const mockPeopleMoves = {
    "commodities": [
      {
        "name": "Sarah Chen",
        "from": "Trafigura",
        "to": "Vitol",
        "role": "Senior Oil Trader",
        "date": "2025-01-15",
        "location": "Singapore",
        "significance": "high",
        "details": "Leading oil trader with 8+ years experience"
      },
      {
        "name": "Michael Rodriguez",
        "from": "Glencore",
        "to": "Mercuria",
        "role": "Head of Metals Trading",
        "date": "2025-01-10",
        "location": "London",
        "significance": "high",
        "details": "Senior metals trader with 12+ years experience"
      },
      {
        "name": "Emma Thompson",
        "from": "Cargill",
        "to": "ADM",
        "role": "Grains Trading Director",
        "date": "2025-01-08",
        "location": "Geneva",
        "significance": "medium",
        "details": "Agricultural commodities specialist"
      }
    ]
  };

  useEffect(() => {
    // Initialize with welcome message
    setMessages([
      {
        id: 1,
        type: 'alice',
        content: "Hello! I'm Alice, your Market Intelligence Assistant. I can help you with salary benchmarking and people moves analysis.\n\nWhat would you like to explore today?",
        timestamp: new Date(),
        showFilters: false  // Filters hidden initially
      }
    ]);
    setShowQuickActions(true);  // Show quick action buttons initially
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Voice recognition setup
  useEffect(() => {
    if (typeof window !== 'undefined' && window.webkitSpeechRecognition) {
      recognitionRef.current = new window.webkitSpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        handleSendMessage(transcript);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
    }
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      if (audioRef.current) audioRef.current.pause();
    };
  }, []);

  // Text-to-speech functionality
  const speakMessage = async (text) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        return;
      }
      setIsSpeaking(true);
      const response = await fetch(`${API_BASE_URL}/api/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text })
      });

      if (!response.ok) {
        throw new Error(`TTS request failed with status ${response.status}`);
      }
      const result = await response.json();
      if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.onended = () => setIsSpeaking(false);
      }
      const audioBlob = await fetch(`data:audio/mp3;base64,${result.audio}`).then(r => r.blob());
      const audioUrl = URL.createObjectURL(audioBlob);
      audioRef.current.src = audioUrl;
      await audioRef.current.play();
    } catch (error) {
      console.warn('Server TTS unavailable; using browser speech:', error);
      try {
        const { speakWithBrowserTts } = await import('../utils/browserSpeech');
        await speakWithBrowserTts(text);
      } catch (fallbackError) {
        console.error('All speech providers failed:', fallbackError);
      } finally {
        setIsSpeaking(false);
      }
    }
  };

  // Speak AI messages
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.type === 'alice' && lastMsg.content) {
        speakMessage(lastMsg.content);
      }
    }
  }, [messages]);

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleQuickAction = (action) => {
    let message = '';
    /** Snapshot of filters for Get Results — sent as JSON so the API does not rely on text alone */
    let executeFiltersPayload = null;
    switch (action) {
      case 'salary':
        message = "I'd like to get salary benchmarking information";
        break;
      case 'people_moves':
        // Start the conversational flow for people moves
        setConversationState('awaiting_industry');
        setPendingQuery('people_moves');
        // Don't send a message, instead show Alice's response asking for industry
        const peopleMovesMessage = {
          id: Date.now(),
          type: 'user',
          content: "I'm interested in finding out the latest talent moves",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, peopleMovesMessage]);

        // Show Alice's response asking for industry
        setTimeout(() => {
          const aliceIndustryPrompt = {
            id: Date.now() + 1,
            type: 'alice',
            content: "Great! I can help you find people moves. What industry are you interested in? You can type it in the search bar below (e.g., 'commodities', 'technology', 'finance') or select from the options.",
            timestamp: new Date(),
            showFilters: false
          };
          setMessages(prev => [...prev, aliceIndustryPrompt]);
          setShowQuickActions(true);  // Show industry selection buttons
        }, 500);
        return; // Don't call handleSendMessage
      case 'industry_commodities':
        message = "commodities";
        break;
      case 'industry_technology':
        message = "technology";
        break;
      case 'industry_finance':
        message = "finance";
        break;
      case 'industry_healthcare':
        message = "healthcare";
        break;
      case 'category_energy':
        setCurrentFilters(prev => {
          const newFilters = { ...prev, 'Category': 'Energy' };
          console.log('🔍 Setting filters:', newFilters);
          return newFilters;
        });
        return;
      case 'category_metals':
        setCurrentFilters(prev => ({ ...prev, 'Category': 'Metals' }));
        return;
      case 'category_agriculture_&_softs':
        setCurrentFilters(prev => ({ ...prev, 'Category': 'Agriculture & Softs' }));
        return;
      case 'category_freight_&_shipping':
        setCurrentFilters(prev => ({ ...prev, 'Category': 'Freight & Shipping' }));
        return;
      case 'category_cross-commodity_&_finance':
        setCurrentFilters(prev => ({ ...prev, 'Category': 'Cross-Commodity & Finance' }));
        return;
      case 'category_carbon_&_renewables':
        setCurrentFilters(prev => ({ ...prev, 'Category': 'Carbon & Renewables' }));
        return;
      case 'category_other_specialties':
        setCurrentFilters(prev => ({ ...prev, 'Category': 'Other Specialties' }));
        return;
      case 'category_all':
        setCurrentFilters(prev => {
          const newFilters = { ...prev };
          delete newFilters['Category'];
          return newFilters;
        });
        return;
      case 'product_lng':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'LNG' }));
        return;
      case 'product_physical_lng':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Physical LNG' }));
        return;
      case 'product_jkm':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'JKM' }));
        return;
      case 'product_ttf':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'TTF' }));
        return;
      case 'product_ffa_derivatives':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'FFA derivatives' }));
        return;
      case 'product_lng_trading':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'LNG Trading' }));
        return;
      case 'product_lng_origination_marketing':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'LNG Origination / Marketing' }));
        return;
      case 'product_lng_shipping_chartering':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'LNG Shipping / Chartering' }));
        return;
      case 'product_lng_naphtha':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'LNG/Naphtha' }));
        return;
      case 'product_lng_gas':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'LNG / Gas' }));
        return;
      case 'product_lng_decarbonisation':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'LNG / Decarbonisation' }));
        return;
      case 'product_structured_gas_origination':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Structured Gas / Origination' }));
        return;
      case 'product_gas_lng':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Gas / LNG' }));
        return;
      case 'product_gas_power':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Gas & Power' }));
        return;
      case 'product_gas_power_lng_credit_risk':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Gas, Power & LNG / Credit Risk' }));
        return;
      case 'product_gas_power_alt':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Gas/Power' }));
        return;
      case 'product_gas_operations':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Gas Operations' }));
        return;
      case 'product_east_gas':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'East Gas' }));
        return;
      case 'product_south_gas':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'South Gas' }));
        return;
      case 'product_interstate_gas':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Interstate Gas' }));
        return;
      case 'product_natural_gas':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Natural Gas' }));
        return;
      case 'product_canada_natural_gas':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Canada Natural Gas' }));
        return;
      case 'product_renewable_natural_gas_environmental':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Renewable Natural Gas / Environmental' }));
        return;
      case 'product_natural_gas_supply':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Natural Gas Supply' }));
        return;
      case 'product_ngl_ngls_lpg':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'NGL / NGLs / LPG' }));
        return;
      case 'product_lpg_swaps':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'LPG Swaps' }));
        return;
      case 'product_lpg':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'LPG' }));
        return;
      case 'product_gulf_coast_ngls':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Gulf Coast NGLs' }));
        return;
      case 'product_crude_oil':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Crude Oil' }));
        return;
      case 'product_wti_crude':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'WTI Crude' }));
        return;
      case 'product_lease_crude_oil':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Lease Crude Oil' }));
        return;
      case 'product_us_crude':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'US Crude' }));
        return;
      case 'product_us_crude_oil':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'US Crude Oil' }));
        return;
      case 'product_crude_supply_trading':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Crude Supply & Trading' }));
        return;
      case 'product_crude_oil_options':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Crude Oil Options' }));
        return;
      case 'product_oil_trading':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Oil Trading' }));
        return;
      case 'product_oil_commodity_futures':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Oil / Commodity Futures' }));
        return;
      case 'product_oil_products_crude_oil':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Oil Products / Crude Oil' }));
        return;
      case 'product_oil_naphtha':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Oil / Naphtha' }));
        return;
      case 'product_oil_derivatives':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Oil Derivatives' }));
        return;
      case 'product_oil_options_derivatives':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Oil Options / Derivatives' }));
        return;
      case 'product_oil_refined_products':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Oil / Refined Products' }));
        return;
      case 'product_light_ends_oil_products_oil_derivatives':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Light Ends / Oil Products / Oil Derivatives' }));
        return;
      case 'product_gasoline_gasoline_aromatics':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Gasoline / Gasoline & Aromatics' }));
        return;
      case 'product_gasoline_distillates_risk':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Gasoline/Distillates (Risk)' }));
        return;
      case 'product_naphtha_lpg':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Naphtha & LPG' }));
        return;
      case 'product_middle_distillates_fuel_oil_distillates_feedstock':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Middle Distillates / Fuel Oil / Distillates / Feedstock' }));
        return;
      case 'product_aviation_oil':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Aviation Oil' }));
        return;
      case 'product_bitumen':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Bitumen' }));
        return;
      case 'product_fuel_oil_heavy_products_residual_fuels':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Fuel Oil / Heavy Products / Residual Fuels' }));
        return;
      case 'product_dob_fuel_oil':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'DOB Fuel Oil' }));
        return;
      case 'product_energy_markets':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Energy Markets' }));
        return;
      case 'product_energy_oil':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Energy / Oil' }));
        return;
      case 'product_energy_fuels':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Energy / Fuels' }));
        return;
      case 'product_energy_derivatives':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Energy / Derivatives' }));
        return;
      case 'product_energy_day_ahead_power':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Energy / Day Ahead Power' }));
        return;
      case 'product_energy_power':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Energy / Power' }));
        return;
      case 'product_energy_retail_trading':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Energy / Retail Trading' }));
        return;
      case 'product_energy_contracts':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Energy Contracts' }));
        return;
      case 'product_energy_commodities':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Energy Commodities' }));
        return;
      case 'product_energy_origination':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Energy Origination' }));
        return;
      case 'product_energy_trading':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Energy Trading' }));
        return;
      case 'product_energy_and_metals_finance':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Energy and Metals (Finance)' }));
        return;
      case 'product_clean_products':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Clean Products' }));
        return;
      case 'product_power':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Power' }));
        return;
      case 'product_us_power_ercot_power_east_power_west_power_northeast_power_south_gas_power_solutions_real_time_power_intraday_power_short_term_power_continental_power':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'US Power / ERCOT Power / East Power / West Power / Northeast Power / South Gas Power Solutions / Real Time Power / Intraday Power / Short Term Power / Continental Power' }));
        return;
      case 'product_european_power_gas_european_natural_gas':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'European Power & Gas / European Natural Gas' }));
        return;
      case 'product_power_quantitative_power_quantitative_analyst':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Power / Quantitative / Power Quantitative Analyst' }));
        return;
      case 'product_power_gas':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Power & Gas' }));
        return;
      case 'product_ppa_renewable_energy':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'PPA / Renewable Energy' }));
        return;
      case 'product_renewable_commodities_fuel_oil':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Renewable Commodities / Fuel Oil' }));
        return;
      case 'product_global_energy_residual_fuels':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Global Energy / Residual Fuels' }));
        return;
      case 'product_power_origination':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Power Origination' }));
        return;
      case 'product_prompt_trading_power_gas':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Prompt Trading (Power/Gas)' }));
        return;
      case 'product_energy_emissions_global_carbon_markets_carbon_environmental_commodities_voluntary_carbon_markets':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Energy/Emissions / Global Carbon Markets / Carbon / Environmental Commodities / Voluntary Carbon Markets' }));
        return;
      case 'product_clean_energy_environmental_carbon_agriculture':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Clean Energy / Environmental / Carbon / Agriculture' }));
        return;
      case 'product_renewable_hydrocarbons':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Renewable Hydrocarbons' }));
        return;
      case 'product_hydrogen_gas':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Hydrogen / Gas' }));
        return;
      case 'product_hefa_feedstock':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'HEFA Feedstock' }));
        return;
      // Add more product cases as needed for other categories
      case 'product_trading':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Trading' }));
        return;
      case 'product_sales':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Sales' }));
        return;
      case 'product_operations':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Operations' }));
        return;
      case 'product_technology':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Technology' }));
        return;
      case 'product_hr':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'HR' }));
        return;
      case 'product_marketing':
        setCurrentFilters(prev => ({ ...prev, 'Product': 'Marketing' }));
        return;
      case 'year_2025':
        setCurrentFilters(prev => ({ ...prev, 'Year': '2025' }));
        return;
      case 'year_2024':
        setCurrentFilters(prev => ({ ...prev, 'Year': '2024' }));
        return;
      case 'year_2023':
        setCurrentFilters(prev => ({ ...prev, 'Year': '2023' }));
        return;
      case 'year_2022':
        setCurrentFilters(prev => ({ ...prev, 'Year': '2022' }));
        return;
      case 'year_all':
        setCurrentFilters(prev => {
          const newFilters = { ...prev };
          delete newFilters['Year'];
          return newFilters;
        });
        return;
      case 'quarter_q1':
        setCurrentFilters(prev => ({ ...prev, 'Quarter': 'Q1' }));
        return;
      case 'quarter_q2':
        setCurrentFilters(prev => ({ ...prev, 'Quarter': 'Q2' }));
        return;
      case 'quarter_q3':
        setCurrentFilters(prev => ({ ...prev, 'Quarter': 'Q3' }));
        return;
      case 'quarter_q4':
        setCurrentFilters(prev => ({ ...prev, 'Quarter': 'Q4' }));
        return;
      case 'quarter_all':
        setCurrentFilters(prev => {
          const newFilters = { ...prev };
          delete newFilters['Quarter'];
          return newFilters;
        });
        return;
      case 'limit_25':
        setCurrentFilters(prev => ({ ...prev, 'Limit': '25' }));
        return;
      case 'limit_50':
        setCurrentFilters(prev => ({ ...prev, 'Limit': '50' }));
        return;
      case 'limit_100':
        setCurrentFilters(prev => ({ ...prev, 'Limit': '100' }));
        return;
      case 'limit_all':
        setCurrentFilters(prev => {
          const newFilters = { ...prev };
          delete newFilters['Limit'];
          return newFilters;
        });
        return;
      case 'clear_company':
        setCurrentFilters(prev => {
          const newFilters = { ...prev };
          delete newFilters['Company'];
          return newFilters;
        });
        return;
      case 'clear_filters':
        setCurrentFilters({});
        return;
            case 'execute_query':
        // Execute the pending query with current filters
        console.log('🔍 Execute query - pendingQuery:', pendingQuery, 'currentFilters:', currentFilters);

        executeFiltersPayload = { ...currentFilters };
        const catLabelForKey = currentFilters['Category'];
        if (catLabelForKey && PEOPLE_MOVE_CATEGORY_TO_KEY[catLabelForKey]) {
          executeFiltersPayload.CategoryKey = PEOPLE_MOVE_CATEGORY_TO_KEY[catLabelForKey];
        }

        // Execute people moves query with filters
        const category = currentFilters['Category'] || '';
        const industry = currentFilters['Industry'] || '';
        const product = currentFilters['Product'] || '';
        const year = currentFilters['Year'] || '';
        const quarter = currentFilters['Quarter'] || '';
        const company = currentFilters['Company'] || '';
        const limit = currentFilters['Limit'] || '';

        // Build query - prioritize category, then industry, then all
        let query;
        if (category) {
          query = `Show me people moves in ${category}`;
        } else if (industry) {
          query = `Show me people moves in ${industry}`;
        } else {
          query = `Show me all people moves`;
        }

        if (product) {
          query += `, ${product}`;
        }
        if (company) {
          query += ` involving ${company}`;
        }
        if (year) {
          query += ` for ${year}`;
        }
        if (quarter) {
          query += ` ${quarter}`;
        }
        if (limit && limit !== 'all') {
          query += ` (limit ${limit} results)`;
        } else if (!limit) {
          // Default to all results if no limit specified
          query += ` (all results)`;
        }

        console.log('🔍 Generated query:', query);
        message = query;
        setConversationState('initial');
        setPendingQuery('');
        break;
      default:
        // Handle dynamic company setting: set_company_Vitol
        if (action.startsWith('set_company_')) {
          const company = action.replace('set_company_', '');
          setCurrentFilters(prev => ({ ...prev, 'Company': company }));
          return;
        }
        return;
    }
    setInput(message);
    if (executeFiltersPayload !== null) {
      handleSendMessage(message, { filtersSnapshot: executeFiltersPayload, fromExecutedQuery: true });
    } else {
      handleSendMessage(message);
    }
  };

  const handleActionButton = (action, move) => {
    let message = '';

    switch (action) {
      case 'contact':
        message = `I'd like to contact ${move.name} who moved from ${move.from_company || move.from} to ${move.to_company || move.to} as ${move.role}. Can you help me with the best approach?`;
        break;
      case 'analyze':
        // Call the analyze endpoint directly
        generateAnalysis(move);
        return; // Don't send a message, just generate the analysis
      case 'followup':
        message = `I want to set up a follow-up reminder for ${move.name}'s move to ${move.to_company || move.to}. When should I check back?`;
        break;
      case 'share':
        // Generate and download the share report
        generateShareReport(move);
        return; // Don't send a message, just generate the report
      default:
        return;
    }

    if (message) {
      handleSendMessage(message);
    }
  };

  // New function to generate analysis
  const generateAnalysis = async (move) => {
    try {
      setIsLoading(true);

      // Call the backend to generate analysis
      const response = await fetch(`${API_BASE_URL}/api/market-intelligence/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAuthToken()}`
        },
        body: JSON.stringify({
          move: move,
          analysis_type: 'comprehensive'
        })
      });

      if (response.ok) {
        const result = await response.json();

        if (result.success && result.analysis) {
          // Format the analysis for display
          const analysisMessage = formatAnalysisForDisplay(result.analysis, move);

          // Add the analysis to the chat
          const analysisChatMessage = {
            id: Date.now() + 1,
            type: 'alice',
            content: analysisMessage,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, analysisChatMessage]);
        } else {
          throw new Error(result.error || 'Failed to generate analysis');
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error generating analysis:', error);

      // Add error message to chat
      const errorMessage = {
        id: Date.now() + 1,
        type: 'alice',
        content: `I apologize, but I encountered an issue generating the analysis. Please try again or contact support if the problem persists.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to format analysis for display
  const formatAnalysisForDisplay = (analysis, move) => {
    const { strategic_insights, market_trends, competitive_intelligence, risk_assessment, recommendations } = analysis;

    let formattedMessage = `## 📊 **Strategic Analysis: ${move.name}**\n\n`;

    // Strategic Insights
    if (strategic_insights && strategic_insights.length > 0) {
      formattedMessage += `### 🎯 **Strategic Insights**\n`;
      strategic_insights.forEach(insight => {
        formattedMessage += `• ${insight}\n`;
      });
      formattedMessage += `\n`;
    }

    // Market Trends
    if (market_trends) {
      formattedMessage += `### 📈 **Market Trends**\n`;
      Object.entries(market_trends).forEach(([key, trend]) => {
        if (key !== 'product_specific') {
          formattedMessage += `**${key.replace(/_/g, ' ').toUpperCase()}**: ${trend.trend}\n`;
          formattedMessage += `*Drivers*: ${trend.drivers.join(', ')}\n`;
          formattedMessage += `*Impact*: ${trend.impact}\n\n`;
        }
      });

      if (market_trends.product_specific) {
        formattedMessage += `**PRODUCT-SPECIFIC TRENDS**: ${market_trends.product_specific.trend}\n`;
        formattedMessage += `*Drivers*: ${market_trends.product_specific.drivers.join(', ')}\n`;
        formattedMessage += `*Impact*: ${market_trends.product_specific.impact}\n\n`;
      }
    }

    // Competitive Intelligence
    if (competitive_intelligence) {
      formattedMessage += `### 🏢 **Competitive Intelligence**\n`;
      Object.entries(competitive_intelligence).forEach(([key, intel]) => {
        formattedMessage += `**${key.replace(/_/g, ' ').toUpperCase()}**\n`;
        formattedMessage += `*Insight*: ${intel.insight}\n`;
        formattedMessage += `*Implication*: ${intel.implication}\n`;
        formattedMessage += `*Action*: ${intel.action}\n\n`;
      });
    }

    // Risk Assessment
    if (risk_assessment) {
      formattedMessage += `### ⚠️ **Risk Assessment**\n`;
      Object.entries(risk_assessment).forEach(([key, risk]) => {
        formattedMessage += `**${key.replace(/_/g, ' ').toUpperCase()}** (${risk.risk_level})\n`;
        formattedMessage += `*Description*: ${risk.description}\n`;
        formattedMessage += `*Mitigation*: ${risk.mitigation}\n\n`;
      });
    }

    // Recommendations
    if (recommendations && recommendations.length > 0) {
      formattedMessage += `### 💡 **Recommendations**\n`;
      recommendations.forEach(category => {
        formattedMessage += `**${category.category}**\n`;
        category.recommendations.forEach(rec => {
          formattedMessage += `• ${rec}\n`;
        });
        formattedMessage += `\n`;
      });
    }

    return formattedMessage;
  };

  // New function to generate and download share report
  const generateShareReport = async (move) => {
    try {
      setIsLoading(true);

      // Call the backend to generate PDF report
      const response = await fetch(`${API_BASE_URL}/api/market-intelligence/share-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAuthToken()}`
        },
        body: JSON.stringify({
          move: move,
          report_type: 'individual_move',
          format: 'pdf', // Request PDF format
          include_insights: true
        })
      });

      if (response.ok) {
        const result = await response.json();

        if (result.success) {
          // Handle PDF format (base64)
          if (result.pdfB64) {
            const link = document.createElement('a');
            link.href = `data:application/pdf;base64,${result.pdfB64}`;
            link.download = `Market_Intelligence_Report_${move.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Add a success message to the chat
            const successMessage = {
              id: Date.now() + 1,
              type: 'alice',
              content: `✅ Report generated successfully! I've created a comprehensive market intelligence report for ${move.name}'s move to ${move.to_company || move.to}. The PDF file has been downloaded to your device.`,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, successMessage]);
          }
          // Fallback to HTML format if PDF not available
          else if (result.pdf_url) {
            const link = document.createElement('a');
            link.href = result.pdf_url;
            link.download = `Market_Intelligence_Report_${move.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            const successMessage = {
              id: Date.now() + 1,
              type: 'alice',
              content: `✅ Report generated successfully! I've created a comprehensive market intelligence report for ${move.name}'s move to ${move.to_company || move.to}. The HTML file has been downloaded to your device.

💡 **To convert to PDF**: Open the file in your browser and press Ctrl+P (or Cmd+P on Mac), then select "Save as PDF".`,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, successMessage]);
          } else {
            throw new Error(result.error || 'Failed to generate report');
          }
        } else {
          throw new Error(result.error || 'Failed to generate report');
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error generating share report:', error);

      // Add error message to chat
      const errorMessage = {
        id: Date.now() + 1,
        type: 'alice',
        content: `I apologize, but I encountered an issue generating the report. Please try again or contact support if the problem persists.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // New function to generate bulk share report for multiple moves
  const generateBulkShareReport = async (moves, industry = null) => {
    try {
      setIsLoading(true);

      const response = await fetch(`${API_BASE_URL}/api/market-intelligence/share-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAuthToken()}`
        },
        body: JSON.stringify({
          moves: moves,
          report_type: 'bulk_moves',
          format: 'pdf', // Request PDF format
          industry: industry,
          include_insights: true,
          include_trends: true
        })
      });

      if (response.ok) {
        const result = await response.json();

        if (result.success) {
          // Handle PDF format (base64)
          if (result.pdfB64) {
            const link = document.createElement('a');
            link.href = `data:application/pdf;base64,${result.pdfB64}`;
            const industryName = industry ? industry.replace(/\s+/g, '_') : 'all_industries';
            link.download = `Market_Intelligence_Bulk_Report_${industryName}_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Add success message
            const successMessage = {
              id: Date.now() + 1,
              type: 'alice',
              content: `✅ Bulk report generated successfully! I've created a comprehensive market intelligence report covering ${moves.length} people moves${industry ? ` in ${industry}` : ''}. The PDF file has been downloaded to your device.`,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, successMessage]);
          }
          // Fallback to HTML format if PDF not available
          else if (result.pdf_url) {
            const link = document.createElement('a');
            link.href = result.pdf_url;
            const industryName = industry ? industry.replace(/\s+/g, '_') : 'all_industries';
            link.download = `Market_Intelligence_Bulk_Report_${industryName}_${new Date().toISOString().split('T')[0]}.html`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            const successMessage = {
              id: Date.now() + 1,
              type: 'alice',
              content: `✅ Bulk report generated successfully! I've created a comprehensive market intelligence report covering ${moves.length} people moves${industry ? ` in ${industry}` : ''}. The HTML file has been downloaded to your device.

💡 **To convert to PDF**: Open the file in your browser and press Ctrl+P (or Cmd+P on Mac), then select "Save as PDF".`,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, successMessage]);
          } else {
            throw new Error(result.error || 'Failed to generate bulk report');
          }
        } else {
          throw new Error(result.error || 'Failed to generate bulk report');
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error generating bulk share report:', error);

      const errorMessage = {
        id: Date.now() + 1,
        type: 'alice',
        content: `I apologize, but I encountered an issue generating the bulk report. Please try again or contact support if the problem persists.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (messageText = null, options = {}) => {
    const { filtersSnapshot = null, fromExecutedQuery = false } = options;
    const text = messageText || input.trim();
    if (!text) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setShowQuickActions(false);

    // Handle conversational flow
    if (conversationState === 'awaiting_industry') {
      // User has selected/typed an industry, now show filter options
      const industry = text.toLowerCase().trim();

      // Normalize industry name
      let normalizedIndustry = industry;
      if (industry.includes('commodities') || industry.includes('commodity')) {
        normalizedIndustry = 'commodities';
      } else if (industry.includes('technology') || industry.includes('tech')) {
        normalizedIndustry = 'technology';
      } else if (industry.includes('finance') || industry.includes('financial')) {
        normalizedIndustry = 'finance';
      }

      setCurrentFilters({ 'Industry': normalizedIndustry.charAt(0).toUpperCase() + normalizedIndustry.slice(1) });
      setConversationState('awaiting_filters');

      // Add Alice's response asking for filters
      const aliceResponse = {
        id: Date.now() + 1,
        type: 'alice',
        content: `Great! I can provide you with people moves in ${normalizedIndustry}. Use the filters below to refine your search by commodity category, specific product/function, year, quarter, company, and result limit.`,
        timestamp: new Date(),
        showFilters: true  // Show filters now that industry is selected
      };
      setMessages(prev => [...prev, aliceResponse]);
      setIsLoading(false);
      setShowQuickActions(false);  // Hide quick actions once filters are shown
      return;
    }

    // Infer filters from free text only when not sending a structured Get Results payload
    if (!fromExecutedQuery) {
      const newFilters = {};
      const lower = text.toLowerCase();
      if (lower.includes('2025')) {
        newFilters['Year'] = '2025';
      } else if (lower.includes('2024')) {
        newFilters['Year'] = '2024';
      } else if (lower.includes('2023')) {
        newFilters['Year'] = '2023';
      } else if (lower.includes('2022')) {
        newFilters['Year'] = '2022';
      }

      if (lower.includes('commodities')) {
        newFilters['Industry'] = 'Commodities';
      }

      if (Object.keys(newFilters).length > 0) {
        setCurrentFilters((prev) => ({ ...prev, ...newFilters }));
      }
    }

    try {
      const response = await simulateMarketIntelligenceAPI(text, { filtersSnapshot });

      console.log('🔍 Response object in handleSendMessage:', response);
      console.log('🔍 response.apiResponse:', response.apiResponse);

      // Add Alice's response
      const aliceMessage = {
        id: Date.now() + 1,
        type: 'alice',
        content: response.message,
        data: response.data,
        apiResponse: response.apiResponse, // Add the apiResponse field
        timestamp: new Date()
      };

      console.log('🔍 aliceMessage object:', aliceMessage);
      setMessages(prev => [...prev, aliceMessage]);

      if (fromExecutedQuery && response.apiResponse?.type === 'people_moves') {
        setCurrentFilters({});
      }

      // Add follow-up message for people moves
      if (response.apiResponse && response.apiResponse.type === 'people_moves') {
        setTimeout(() => {
          const followUpMessage = {
            id: Date.now() + 2,
            type: 'alice',
            content: `What would you like to do with this intelligence? You can:
• 📞 Contact these professionals directly
• 📊 Get deeper analysis and insights
• 🔄 Set up follow-up reminders
• 📤 Share individual reports with your team
• 📋 Generate a comprehensive bulk report for all moves

Just let me know what interests you most!`,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, followUpMessage]);
        }, 1000);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'alice',
        content: "I apologize, but I'm having trouble processing your request right now. Please try again.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const simulateMarketIntelligenceAPI = async (query, extra = {}) => {
    const { filtersSnapshot } = extra;
    try {
      const payload = { message: query };
      if (filtersSnapshot && typeof filtersSnapshot === 'object' && Object.keys(filtersSnapshot).length > 0) {
        payload.filters = filtersSnapshot;
      }

      const response = await fetch(`${API_BASE_URL}/api/market-intelligence/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAuthToken()}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(`API call failed: ${response.status}${errText ? ` — ${errText.slice(0, 200)}` : ''}`);
      }

      const result = await response.json();

      if (result.success) {
        console.log('✅ API Response received:', result);
        return {
          message: result.response,
          data: result.data,
          apiResponse: result
        };
      }
      throw new Error(result.error || 'Unknown error');
    } catch (error) {
      console.error('Market Intelligence API error:', error);
      throw error;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatSalaryData = (data) => {
    // Handle both old mock data structure and new backend structure
    const baseSalary = data.base_salary || data.salary_data?.base_salary;
    const totalComp = data.total_compensation || data.salary_data?.total_comp;
    const bonusRange = data.bonus_range || data.salary_data?.bonus_percentage;
    const marketTrend = data.market_trend || data.salary_data?.market_trend;
    const currency = data.base_salary?.currency || 'USD';

    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100">Base Salary</h4>
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
              {currency} {baseSalary?.median?.toLocaleString() || 'N/A'}
            </p>
            <p className="text-sm text-blue-600 dark:text-blue-400">
              Range: {currency} {baseSalary?.min?.toLocaleString() || 'N/A'} - {baseSalary?.max?.toLocaleString() || 'N/A'}
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100">Total Compensation</h4>
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
              {currency} {totalComp?.median?.toLocaleString() || 'N/A'}
            </p>
            <p className="text-sm text-blue-600 dark:text-blue-400">
              Range: {currency} {totalComp?.min?.toLocaleString() || 'N/A'} - {totalComp?.max?.toLocaleString() || 'N/A'}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100">Bonus Structure</h4>
            <p className="text-lg font-semibold text-blue-700 dark:text-blue-300">
              {bonusRange?.median ? `${bonusRange.median}% of base` : 'N/A'}
            </p>
            <p className="text-sm text-blue-600 dark:text-blue-400">
              Range: {bonusRange?.min || 'N/A'}% - {bonusRange?.max || 'N/A'}%
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100">Market Trend</h4>
            <p className="text-lg font-semibold text-green-600 dark:text-green-400">
              {marketTrend || 'N/A'}
            </p>
            <p className="text-sm text-blue-600 dark:text-blue-400">
              Data source: {data.data_source || 'Market Intelligence'}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Component for displaying people moves with company logos
  const PeopleMovesDisplay = ({ moves }) => {
    const [logoUrls, setLogoUrls] = useState({});

    // Fetch company logos when component mounts or moves change
    useEffect(() => {
      const fetchLogos = async () => {
        const newLogoUrls = {};
        for (const move of moves) {
          const fromCompany = move.from_company || move.from;
          const toCompany = move.to_company || move.to;

          if (fromCompany && !logoUrls[fromCompany]) {
            newLogoUrls[fromCompany] = await getCompanyLogo(fromCompany);
          }
          if (toCompany && !logoUrls[toCompany]) {
            newLogoUrls[toCompany] = await getCompanyLogo(toCompany);
          }
        }
        setLogoUrls(prev => ({ ...prev, ...newLogoUrls }));
      };

      fetchLogos();
    }, [moves]);

    // Extract industry from moves for bulk report
    const industry = moves.length > 0 ? moves[0].industry : null;

    return (
      <div className="space-y-4">
        {/* Bulk Share Button */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {moves.length} people moves{industry ? ` in ${industry}` : ''}
          </div>
          <button
            onClick={() => generateBulkShareReport(moves, industry)}
            className="flex items-center space-x-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors duration-200"
          >
            <span>📋</span>
            <span>Generate Bulk Report</span>
          </button>
        </div>

        {moves && moves.length > 0 ? moves.map((move, index) => (
          <div key={index} className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-4 border-l-4 border-purple-500">
            <div className="flex items-start space-x-4">
              {/* Profile Picture */}
              <div className="flex-shrink-0">
                <img
                  src={getProfilePictureFromLinkedIn(move.linkedin_url || move.from_linkedin_url)}
                  alt={`${move.name}'s profile`}
                  className="w-12 h-12 rounded-full border-2 border-white shadow-sm"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(move.name)}&background=random&size=48`;
                  }}
                />
              </div>

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 dark:text-white text-lg truncate">
                      {move.name}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      {move.role}
                    </p>
                    {move.category ? (
                      <p className="text-xs text-purple-600 dark:text-purple-300 mt-1 font-medium">
                        {formatPeopleMoveCategoryLabel(move.category)}
                      </p>
                    ) : null}
                  </div>

                  {/* Significance Badge */}
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold ml-2 ${(move.significance || '').toLowerCase() === 'high'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                    {(move.significance || '').toLowerCase() === 'high' ? 'High Impact' : 'Medium Impact'}
                  </div>
                </div>

                {/* Company Transition */}
                <div className="flex items-center space-x-3 mt-3">
                  {/* From Company */}
                  <div className="flex items-center space-x-2">
                    <img
                      src={logoUrls[move.from_company || move.from] || `https://ui-avatars.com/api/?name=${encodeURIComponent(move.from_company || move.from)}&background=random&size=24&bold=true`}
                      alt={`${move.from_company || move.from} logo`}
                      className="w-6 h-6 rounded"
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(move.from_company || move.from)}&background=random&size=24&bold=true`;
                      }}
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                      {move.from_company || move.from}
                    </span>
                  </div>

                  {/* Arrow */}
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>

                  {/* To Company */}
                  <div className="flex items-center space-x-2">
                    <img
                      src={logoUrls[move.to_company || move.to] || `https://ui-avatars.com/api/?name=${encodeURIComponent(move.to_company || move.to)}&background=random&size=24&bold=true`}
                      alt={`${move.to_company || move.to} logo`}
                      className="w-6 h-6 rounded"
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(move.to_company || move.to)}&background=random&size=24&bold=true`;
                      }}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                      {move.to_company || move.to}
                    </span>
                  </div>
                </div>

                {/* Additional Details */}
                <div className="mt-3 space-y-1">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    📍 {move.location} • 📅 {new Date(move.date).toLocaleDateString()}
                  </p>
                  {move.product_traded && (
                    <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                      🏷️ {move.product_traded}
                    </p>
                  )}
                  {move.details && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                      {move.details}
                    </p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="mt-4 flex space-x-2">
                  <button
                    onClick={() => handleActionButton('contact', move)}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium py-2 px-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-1"
                  >
                    <span>📞</span>
                    <span>Contact</span>
                  </button>
                  <button
                    onClick={() => handleActionButton('analyze', move)}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white text-xs font-medium py-2 px-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-1"
                  >
                    <span>📊</span>
                    <span>Analyze</span>
                  </button>
                  <button
                    onClick={() => handleActionButton('followup', move)}
                    className="flex-1 bg-purple-500 hover:bg-purple-600 text-white text-xs font-medium py-2 px-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-1"
                  >
                    <span>🔄</span>
                    <span>Follow Up</span>
                  </button>
                  <button
                    onClick={() => handleActionButton('share', move)}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium py-2 px-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-1"
                  >
                    <span>📤</span>
                    <span>Share</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )) : null}
      </div>
    );
  };

  const FilterOptions = ({ onFilterSelect, currentFilters }) => {
    // Local state for company input to prevent focus loss during typing
    const currentCompany = currentFilters['Company'] || '';
    const [companyInput, setCompanyInput] = useState(currentCompany);

    // Sync local state when currentFilters changes externally
    useEffect(() => {
      setCompanyInput(currentCompany);
    }, [currentCompany]);

    const commodityCategories = {
      'Energy': {
        icon: '⚡',
        color: 'orange',
        products: [
          'LNG', 'Physical LNG', 'JKM', 'TTF', 'FFA derivatives', 'LNG Trading', 'LNG Origination / Marketing',
          'LNG Shipping / Chartering', 'LNG/Naphtha', 'LNG / Gas', 'LNG / Decarbonisation', 'Structured Gas / Origination',
          'Gas / LNG', 'Gas & Power', 'Gas, Power & LNG / Credit Risk', 'Gas/Power', 'Gas Operations', 'East Gas',
          'South Gas', 'Interstate Gas', 'Natural Gas', 'Canada Natural Gas', 'Renewable Natural Gas / Environmental',
          'Natural Gas Supply', 'NGL / NGLs / LPG', 'LPG Swaps', 'LPG', 'Gulf Coast NGLs', 'Crude Oil', 'WTI Crude',
          'Lease Crude Oil', 'US Crude', 'US Crude Oil', 'Crude Supply & Trading', 'Crude Oil Options', 'Oil Trading',
          'Oil / Commodity Futures', 'Oil Products / Crude Oil', 'Oil / Naphtha', 'Oil Derivatives', 'Oil Options / Derivatives',
          'Oil / Refined Products', 'Light Ends / Oil Products / Oil Derivatives', 'Gasoline / Gasoline & Aromatics',
          'Gasoline/Distillates (Risk)', 'Naphtha & LPG', 'Middle Distillates / Fuel Oil / Distillates / Feedstock',
          'Aviation Oil', 'Bitumen', 'Fuel Oil / Heavy Products / Residual Fuels', 'DOB Fuel Oil', 'Energy Markets',
          'Energy / Oil', 'Energy / Fuels', 'Energy / Derivatives', 'Energy / Day Ahead Power', 'Energy / Power',
          'Energy / Retail Trading', 'Energy Contracts', 'Energy Commodities', 'Energy Origination', 'Energy Trading',
          'Energy and Metals (Finance)', 'Clean Products', 'Power', 'US Power / ERCOT Power / East Power / West Power / Northeast Power / South Gas Power Solutions / Real Time Power / Intraday Power / Short Term Power / Continental Power',
          'European Power & Gas / European Natural Gas', 'Power / Quantitative / Power Quantitative Analyst', 'Power & Gas',
          'PPA / Renewable Energy', 'Renewable Commodities / Fuel Oil', 'Global Energy / Residual Fuels', 'Power Origination',
          'Prompt Trading (Power/Gas)', 'Energy/Emissions / Global Carbon Markets / Carbon / Environmental Commodities / Voluntary Carbon Markets',
          'Clean Energy / Environmental / Carbon / Agriculture', 'Renewable Hydrocarbons', 'Hydrogen / Gas', 'HEFA Feedstock'
        ]
      },
      'Metals': {
        icon: '🔧',
        color: 'blue',
        products: [
          'Base Metals (Copper, Lead, Zinc, Aluminium, Nickel, Cobalt)', 'Precious Metals (Gold, Silver, Platinum, Palladium)',
          'Ferro Alloys / Bulk Ores & Alloys', 'Steel / Raw Materials / Stainless Steel / Speciality Alloys',
          'Concentrates / Refined Metals', 'Minor Metals / Non-Ferrous Metals', 'Metallic Ore / Iron Ore',
          'Battery Metals (Copper, Cobalt, Lithium)', 'Billets / Slabs / Wire Rod', 'Cement / Clinker',
          'Scrap & Secondary Metals', 'Climate Trading / Steel'
        ]
      },
      'Agriculture & Softs': {
        icon: '🌾',
        color: 'green',
        products: [
          'Grains & Oilseeds (Wheat, Corn, Soybeans, Rice)', 'Organic Grain', 'Pulses', 'Edible Oils / Softseed Oil / Vegetable Oils / Palm Oil / Canola Oil',
          'Biofuels / Ethanol / Renewable Fuels & Credits / Bioenergy', 'Coffee (Green Coffee, Specialty Coffee) / Cocoa',
          'Sugar', 'Rubber', 'Livestock', 'Fertilizer / Agri-inputs', 'Dairy', 'HVO Feedstock Supply',
          'Oils, FOREX & Milling By-Products', 'Feed Components / Meals', 'Agri-commodities Procurement / Finance'
        ]
      },
      'Freight & Shipping': {
        icon: '🚢',
        color: 'purple',
        products: [
          'Dry Bulk / Tankers / CPP / VLCC', 'LNG Freight / LNG Chartering / Small Scale LNG Bunkering', 'LPG Freight',
          'Shipbroking / Chartering / S&P Shipbroking', 'Bunker / Marine Fuel / Lubricants / Fuel Procurement',
          'Freight Trading / Risk / Logistics', 'Tanker Broking (Crude / Products)', 'Marine Sales / Bunker',
          'Land Fuels / Bunker'
        ]
      },
      'Cross-Commodity & Finance': {
        icon: '💼',
        color: 'indigo',
        products: [
          'Cross-Commodity Trading / Strategy', 'Multi-Commodity / Macro Strategy', 'Commodity Derivatives / Options (Oil, Gas, Metals, Agriculture)',
          'Commodity Transition', 'Structured Commodity Finance', 'Structured Trade Finance', 'Trade & Commodity Finance',
          'Commodity Procurement & Sales', 'Commodity Index / QIS Trading', 'Commodities Origination', 'Commodities Structuring',
          'Commodity Broker / Hedge Fund Sales', 'Commodities & Carbon', 'Global Trading (Multi-Asset)'
        ]
      },
      'Carbon & Renewables': {
        icon: '🌱',
        color: 'emerald',
        products: [
          'Carbon Markets (Compliance & Voluntary)', 'Environmental Commodities / Products', 'Renewable Energy (PPA, REC)',
          'Renewable Feedstocks', 'Clean Energy Transition', 'Renewable Hydrocarbons', 'Renewable Natural Gas',
          'Renewable Commodities / Fuel Oil'
        ]
      },
      'Other Specialties': {
        icon: '🎯',
        color: 'gray',
        products: [
          'Energy Analytics / Meteorology', 'Risk Management (Market, Credit, Portfolio)', 'Hedging / Portfolio Operations',
          'Market Supervision (Carbon)', 'Origination (Energy, Gas, Environmental)', 'Physical Trading (Any Commodity)'
        ]
      }
    };

    const getColorClasses = (color) => {
      const colorMap = {
        orange: 'bg-orange-100 hover:bg-orange-200 dark:bg-orange-900 dark:hover:bg-orange-800 text-orange-700 dark:text-orange-200',
        blue: 'bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-200',
        green: 'bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800 text-green-700 dark:text-green-200',
        purple: 'bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800 text-purple-700 dark:text-purple-200',
        indigo: 'bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900 dark:hover:bg-indigo-800 text-indigo-700 dark:text-indigo-200',
        emerald: 'bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900 dark:hover:bg-emerald-800 text-emerald-700 dark:text-emerald-200',
        gray: 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200'
      };
      return colorMap[color] || colorMap.gray;
    };

    return (
      <div className="mt-4 space-y-6">
        {/* Commodity Categories */}
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Select Commodity Category (Optional):</p>
          <div className="mb-2">
            <button
              onClick={() => onFilterSelect('category_all')}
              className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                !currentFilters['Category']
                  ? 'bg-gray-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200'
              }`}
            >
              🌐 All Categories
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.entries(commodityCategories).map(([category, config]) => (
              <button
                key={category}
                onClick={() => onFilterSelect(`category_${category.toLowerCase().replace(/\s+/g, '_')}`)}
                className={`p-3 rounded-lg transition-colors text-sm font-medium ${
                  currentFilters['Category'] === category
                    ? 'ring-2 ring-orange-500 ' + getColorClasses(config.color)
                    : getColorClasses(config.color)
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{config.icon}</span>
                  <span>{category}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Product Selection - Show when category is selected */}
        {currentFilters['Category'] && commodityCategories[currentFilters['Category']] && (
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Select Specific Product in {currentFilters['Category']}:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-60 overflow-y-auto">
              {commodityCategories[currentFilters['Category']].products.map((product) => (
                <button
                  key={product}
                  onClick={() => onFilterSelect(`product_${product.toLowerCase().replace(/[^a-z0-9]/g, '_')}`)}
                  className={`p-2 rounded-lg transition-colors text-xs ${getColorClasses(commodityCategories[currentFilters['Category']].color)}`}
                >
                  {product}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Year Filters */}
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Year:</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => onFilterSelect('year_2025')}
              className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                currentFilters['Year'] === '2025'
                  ? 'bg-green-500 text-white'
                  : 'bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800 text-green-700 dark:text-green-200'
              }`}
            >
              2025 (Latest)
            </button>
            <button
              onClick={() => onFilterSelect('year_2024')}
              className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                currentFilters['Year'] === '2024'
                  ? 'bg-blue-500 text-white'
                  : 'bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-200'
              }`}
            >
              2024
            </button>
            <button
              onClick={() => onFilterSelect('year_2023')}
              className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                currentFilters['Year'] === '2023'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900 dark:hover:bg-yellow-800 text-yellow-700 dark:text-yellow-200'
              }`}
            >
              2023
            </button>
            <button
              onClick={() => onFilterSelect('year_2022')}
              className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                currentFilters['Year'] === '2022'
                  ? 'bg-red-500 text-white'
                  : 'bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:hover:bg-red-800 text-red-700 dark:text-red-200'
              }`}
            >
              2022
            </button>
            <button
              onClick={() => onFilterSelect('year_all')}
              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors text-sm"
            >
              All Years
            </button>
          </div>
        </div>

        {/* Quarter Filters - Only show if year is selected */}
        {currentFilters['Year'] && (
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Quarter (Optional):</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => onFilterSelect('quarter_q1')}
                className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                  currentFilters['Quarter'] === 'Q1'
                    ? 'bg-purple-500 text-white'
                    : 'bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800 text-purple-700 dark:text-purple-200'
                }`}
              >
                Q1 (Jan-Mar)
              </button>
              <button
                onClick={() => onFilterSelect('quarter_q2')}
                className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                  currentFilters['Quarter'] === 'Q2'
                    ? 'bg-purple-500 text-white'
                    : 'bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800 text-purple-700 dark:text-purple-200'
                }`}
              >
                Q2 (Apr-Jun)
              </button>
              <button
                onClick={() => onFilterSelect('quarter_q3')}
                className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                  currentFilters['Quarter'] === 'Q3'
                    ? 'bg-purple-500 text-white'
                    : 'bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800 text-purple-700 dark:text-purple-200'
                }`}
              >
                Q3 (Jul-Sep)
              </button>
              <button
                onClick={() => onFilterSelect('quarter_q4')}
                className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                  currentFilters['Quarter'] === 'Q4'
                    ? 'bg-purple-500 text-white'
                    : 'bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800 text-purple-700 dark:text-purple-200'
                }`}
              >
                Q4 (Oct-Dec)
              </button>
              <button
                onClick={() => onFilterSelect('quarter_all')}
                className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                  !currentFilters['Quarter']
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200'
                }`}
              >
                All Quarters
              </button>
            </div>
          </div>
        )}

        {/* Company Filter */}
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Company (Optional):</p>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="e.g., Vitol, Trafigura, Glencore"
              value={companyInput}
              onChange={(e) => {
                // Update local state immediately for smooth typing
                const value = e.target.value;
                setCompanyInput(value);
              }}
              onBlur={(e) => {
                // Update filter state when user finishes typing (on blur)
                const company = e.target.value.trim();
                if (company) {
                  onFilterSelect(`set_company_${company}`);
                } else {
                  onFilterSelect('clear_company');
                }
              }}
              onKeyDown={(e) => {
                // Update filter state when user presses Enter
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const company = e.target.value.trim();
                  if (company) {
                    onFilterSelect(`set_company_${company}`);
                  } else {
                    onFilterSelect('clear_company');
                  }
                  e.target.blur(); // Remove focus after Enter
                }
              }}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
            />
            {currentFilters['Company'] && (
              <button
                onClick={() => onFilterSelect('clear_company')}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors text-sm"
              >
                Clear
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Matches moves where the company appears in either "From" or "To" company
          </p>
        </div>

        {/* Result Limit Selector */}
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Number of Results:</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => onFilterSelect('limit_25')}
              className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                currentFilters['Limit'] === '25'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900 dark:hover:bg-indigo-800 text-indigo-700 dark:text-indigo-200'
              }`}
            >
              25 Results
            </button>
            <button
              onClick={() => onFilterSelect('limit_50')}
              className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                currentFilters['Limit'] === '50'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900 dark:hover:bg-indigo-800 text-indigo-700 dark:text-indigo-200'
              }`}
            >
              50 Results
            </button>
            <button
              onClick={() => onFilterSelect('limit_100')}
              className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                currentFilters['Limit'] === '100'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900 dark:hover:bg-indigo-800 text-indigo-700 dark:text-indigo-200'
              }`}
            >
              100 Results
            </button>
            <button
              onClick={() => onFilterSelect('limit_all')}
              className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                !currentFilters['Limit']
                  ? 'bg-indigo-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200'
              }`}
            >
              All Results
            </button>
          </div>
        </div>

        {/* Execute Query Button */}
        <div className="pt-2">
          <button
            onClick={() => onFilterSelect('execute_query')}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors font-medium"
          >
            🔍 Get Results
          </button>
        </div>
      </div>
    );
  };

  const formatPeopleMoves = (data) => {
    // Handle both old mock data structure and new backend structure
    console.log('formatPeopleMoves received:', data);
    const movesArray = Array.isArray(data) ? data : data?.moves || [];
    console.log('movesArray:', movesArray);
    return <PeopleMovesDisplay moves={movesArray} />;
  };

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page header card */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200/80 rounded-xl shadow-xs mb-6">
          <div className="px-4 sm:px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-gradient-to-r from-orange-400 to-orange-600 rounded-lg flex items-center justify-center">
                  <MarketIntelligenceIcon />
                </div>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Market Intelligence
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Powered by Alice AI
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-50 dark:bg-gray-900/50">
                <button
                  type="button"
                  onClick={() => setMiView('chat')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    miView === 'chat'
                      ? 'bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Chat
                </button>
                <button
                  type="button"
                  onClick={() => setMiView('graph')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    miView === 'graph'
                      ? 'bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Flow graph
                </button>
                <button
                  type="button"
                  onClick={() => setMiView('talent')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    miView === 'talent'
                      ? 'bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Talent pool
                </button>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Back to dashboard"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {miView === 'graph' && (
          <div className="bg-white dark:bg-gray-900 border border-gray-200/80 rounded-xl shadow-xs p-4 mb-6 max-w-7xl mx-auto">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Company-to-company talent flows from people moves (commodities). Node size reflects the selected mode; colors are clusters.
            </p>
            <MarketIntelligenceGraph />
          </div>
        )}

        {miView === 'talent' && (
          <div className="bg-white dark:bg-gray-900 border border-gray-200/80 rounded-xl shadow-xs p-4 mb-6 max-w-7xl mx-auto">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Talent Pool Insights maps candidates around a selected job using proximity scoring (semantic + tags). Click nodes to view a candidate snapshot.
            </p>
            <TalentPoolInsights />
          </div>
        )}

        {/* Chat Container */}
        {miView === 'chat' && (
        <div className="bg-white dark:bg-gray-900 border border-gray-200/80 rounded-xl shadow-xs overflow-hidden flex flex-col min-h-[60vh] max-w-4xl mx-auto">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-3xl ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
                  {message.type === 'alice' && (
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-semibold">A</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                          <p className="text-gray-900 dark:text-white">{message.content}</p>

                          {/* Show filter options when Alice asks for them */}
                          {message.showFilters && (
                            <FilterOptions onFilterSelect={handleQuickAction} currentFilters={currentFilters} />
                          )}

                          {message.data && (
                            <div className="mt-4">
                              {message.data.type === 'salary_benchmarking' && formatSalaryData(message.data)}
                              {message.data.type === 'people_moves' && formatPeopleMoves(message.data)}
                              {message.data.type === 'salary_benchmark' && formatSalaryData(message.data)}
                            </div>
                          )}
                          {/* Handle data that comes directly from API response */}
                          {console.log('🔍 Checking message.apiResponse:', message.apiResponse)}
                          {console.log('🔍 message.apiResponse.data:', message.apiResponse?.data)}
                          {message.apiResponse && message.apiResponse.data && (
                            <div className="mt-4">
                              {console.log('🔍 Rendering apiResponse:', message.apiResponse)}
                              {message.apiResponse.type === 'salary_benchmarking' && formatSalaryData(message.apiResponse.data)}
                              {message.apiResponse.type === 'people_moves' && formatPeopleMoves(message.apiResponse.data)}
                              {message.apiResponse.type === 'salary_benchmark' && formatSalaryData(message.apiResponse.data)}
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {message.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  )}
                  {message.type === 'user' && (
                    <div className="flex items-start space-x-3">
                      <div className="flex-1">
                        <div className="bg-orange-500 text-white rounded-lg p-4">
                          <p>{message.content}</p>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                          {message.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-gray-600 text-sm font-semibold">
                            {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}



            {isLoading && (
              <div className="flex justify-start">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">A</span>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Active Filters Display */}
          {console.log('🔍 Current filters:', currentFilters, 'Length:', Object.keys(currentFilters).length)}
          {Object.keys(currentFilters).length > 0 && (
            <div className="px-4 pb-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">Active Filters:</p>
                  <button
                    onClick={() => handleQuickAction('clear_filters')}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 underline"
                  >
                    Clear All
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(currentFilters).map(([key, value]) => (
                    <span key={key} className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-xs rounded-full">
                      {key}: {value}
                      <button
                        onClick={() => {
                          const newFilters = { ...currentFilters };
                          delete newFilters[key];
                          setCurrentFilters(newFilters);
                        }}
                        className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          {showQuickActions && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Quick Actions:</p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => handleQuickAction('salary')}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-200 rounded-lg transition-colors"
                >
                  <SalaryIcon />
                  <span>Salary Benchmarking</span>
                </button>
                <button
                  onClick={() => handleQuickAction('people_moves')}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800 text-purple-700 dark:text-purple-200 rounded-lg transition-colors"
                >
                  <PeopleIcon />
                  <span>People Moves</span>
                </button>
              </div>

              {/* Industry Options - Show when user wants people moves */}
              {conversationState === 'awaiting_industry' && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">What industry are you interested in?</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleQuickAction('industry_commodities')}
                      className="px-4 py-2 bg-orange-100 hover:bg-orange-200 dark:bg-orange-900 dark:hover:bg-orange-800 text-orange-700 dark:text-orange-200 rounded-lg transition-colors"
                    >
                      🏭 Commodities
                    </button>
                    <button
                      onClick={() => handleQuickAction('industry_technology')}
                      className="px-4 py-2 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-200 rounded-lg transition-colors"
                    >
                      💻 Technology
                    </button>
                    <button
                      onClick={() => handleQuickAction('industry_finance')}
                      className="px-4 py-2 bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800 text-green-700 dark:text-green-200 rounded-lg transition-colors"
                    >
                      💰 Finance
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Input Area */}
          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
            <div className="flex space-x-3">
              <div className="flex-1">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me about salary benchmarking or people moves..."
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                  rows="1"
                  disabled={isLoading}
                />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={isListening ? stopListening : startListening}
                  className={`p-3 rounded-lg transition-colors ${isListening
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300'
                    }`}
                  disabled={isLoading || isSpeaking}
                >
                  <VoiceIcon />
                </button>
                <button
                  onClick={() => handleSendMessage()}
                  disabled={!input.trim() || isLoading}
                  className="p-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <SendIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
